import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { LegalApi, ViError } from '@vi-iot/client';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthStore } from '../auth/auth.store';
import { SUPPORTED_LANGUAGE } from '../settings';
import { LanguageStore } from '../settings/language/language.store';

import { DocumentsActions } from './documents.actions';
import { DocumentsStore } from './documents.store';

@Injectable()
export class DocumentsEffects {
  getDocuments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.getDocuments),
      concatLatestFrom(() => [
        this.languageStore.languageCode$,
        this.authStore.userAddressCountryCode$,
        this.authStore.userLanguageCode$,
      ]),
      switchMap(([_action, languageCode, userAddressCountryCode, userLanguageCode]) => {
        return this.legalApi
          .getAppDocuments(
            environment.legalDocuments.appId,
            this.getSupportedCountryCode(userAddressCountryCode, userLanguageCode, languageCode)
          )
          .pipe(
            map((response) =>
              DocumentsActions.getDocumentsSuccess({
                documents: response.documents,
              })
            ),
            catchError(() => of(DocumentsActions.getDocumentsError()))
          );
      })
    );
  });

  getDataUsageAgreementDocument$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.dataUsageAgreementDocument.attempt),
      concatLatestFrom(() => this.authStore.getCountryCode()),
      switchMap(([, companyCountryCode]) => {
        return this.legalApi
          .getAppDocuments(environment.legalDocuments.EUDataUsageAgreementAppId, companyCountryCode)
          .pipe(
            map((response) =>
              DocumentsActions.dataUsageAgreementDocument.success({
                documents: response.documents,
              })
            ),
            catchError((error: ViError) =>
              of(DocumentsActions.dataUsageAgreementDocument.error({ error }))
            )
          );
      })
    );
  });

  getUpdatedDocuments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.getUpdatedDocuments),
      concatLatestFrom(() => this.languageStore.languageCode$),
      switchMap(([action, languageCode]) => {
        const language: string = this.getLanguage(languageCode);
        const userCountryCode: string = action.userCountryCode || language;

        return this.legalApi
          .getUpdatedDocuments(environment.legalDocuments.appId, userCountryCode, language)
          .pipe(
            map((response) =>
              DocumentsActions.getUpdatedDocumentsSuccess({
                updatedDocuments: response.documents,
              })
            ),
            catchError(() => of(DocumentsActions.getUpdatedDocumentsError()))
          );
      })
    );
  });

  agreeOnDocuments$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DocumentsActions.agreeOnDocuments),
        concatLatestFrom(() => this.documentsStore.state$),
        map(([_action, documents]) => {
          documents.documentsUpdated.forEach((doc) => {
            this.store.dispatch(DocumentsActions.postAgreement({ revisionId: doc.revisionId }));
          });
        })
      );
    },
    { dispatch: false }
  );

  postAgreement$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.postAgreement),
      concatLatestFrom(() => this.authStore.state$),
      mergeMap(([action, auth]) =>
        this.legalApi
          .createDocumentAgreement(auth.me.id, action.revisionId)
          .pipe(map(() => DocumentsActions.postAgreementSuccess()))
      )
    );
  });

  getXotaAgreementStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DocumentsActions.getXotaAgreementStatus),
      mergeMap((action) =>
        this.legalApi.documentAgreementXota(action.appId, action.installationId).pipe(
          map((response) =>
            DocumentsActions.getXotaAgreementStatusSuccess({ xotaMode: response.xotaMode })
          ),
          catchError(() => of(DocumentsActions.getXotaAgreementStatusError()))
        )
      )
    );
  });

  private getLanguage(languageCode?: SUPPORTED_LANGUAGE): string {
    return languageCode ? languageCode.slice(-2) : 'DE';
  }

  private isLanguageOrCountryCodeSupported(code: string): boolean {
    return Object.values(SUPPORTED_LANGUAGE).some((value) =>
      value.toUpperCase().includes(code.toUpperCase())
    );
  }

  private getSupportedCountryCode(
    userAddressCountryCode: string,
    userLanguageCode: string,
    languageCode: SUPPORTED_LANGUAGE
  ): string {
    if (this.isLanguageOrCountryCodeSupported(userAddressCountryCode)) {
      return userAddressCountryCode.toUpperCase();
    }

    if (this.isLanguageOrCountryCodeSupported(userLanguageCode)) {
      return this.getLanguage(SUPPORTED_LANGUAGE[userLanguageCode]);
    }

    return this.getLanguage(languageCode);
  }

  constructor(
    private actions$: Actions,
    private languageStore: LanguageStore,
    private authStore: AuthStore,
    private documentsStore: DocumentsStore,
    private legalApi: LegalApi,
    private store: Store
  ) {}
}
